import { FormControl, Grid } from "@mui/material";
import { ButtonCancel } from "components/buttons";
import { ButtonApplyFilter } from "components/buttons/ButtonApplyFilter";
import { MprcInputStyles } from "components/filter/MprcInputStyles";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { TrainingContentTypeResponse } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IAdvFilterPickerProps } from "views/general/AdvancedFilter/interfaces";

////////////////////////////////////////////////////////////////////////////////

export const TrainingContentTypePicker: React.FC<IAdvFilterPickerProps> = (props) => {

    const api = useCoreApi();
    const { t } = useTranslation("shared");
    const { filter, onApply, onCancel } = props;

    const [options, setOptions] = useState<TrainingContentTypeResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selected, setSelected] = useState<AutoCompleteDataModel[]>([]);

    const inputRef = useRef<HTMLInputElement>(undefined);
    const multiSelect = filter.canRepeat ?? false;

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const getSuggestions = (searchText: string, offset: number, take: number): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
            searchText = searchText.toLowerCase();
            resolve({
                items: options
                    .filter(x => x.name.toLowerCase().includes(searchText))
                    .map(item => ({ key: item.value, label: item.name, canDelete: true } as AutoCompleteDataModel)),
                totalRows: options.length
            });
        });
    };

    ///
    const handleApply = () => {
        if (selected.length > 0) {
            const newFilter = selected.map(item => ({
                command: filter!.command,
                optionalGroup: filter?.optionGroup,
                text: `${filter?.displayName.replace("...", "")}: ${item.label}`,
                value: item.key,
                quickFilter: null,
                period: null,
                range: null,
                includeDescendants: null
            }));
            onApply(newFilter, filter);
        }
    };

    useEffect(() => {
        api.v1.lmsSelectors.trainingContentTypes()
            .then(response => setOptions(response.data))
            .catch(e => MprcRequestSnackbarErrorReport(e))
            .finally(() => setLoading(false))
            ;
    }, []);

    useEffect(() => {
        (inputRef as any)?.current?.focus();
    }, [inputRef]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <Grid container alignItems="flex-end" spacing={2}>
            <Grid size="grow">
                <FormControl fullWidth>
                    <MprcAutoComplete
                        label={filter.placeholder ?? t(`AdvFilter.Picker.TrainingType.Placeholder${multiSelect ? "_multi" : ""}`)}
                        fullWidth
                        autoFocus
                        multiSelect={multiSelect}
                        disabled={loading}
                        inputRef={inputRef}
                        selectedItems={selected}
                        onChange={(items) => setSelected(items)}
                        onGetSuggestions={getSuggestions}
                        onApply={handleApply}
                        onCancel={onCancel}
                        sx={MprcInputStyles.filter}
                    />
                </FormControl>
            </Grid>
            <Grid>
                <ButtonApplyFilter
                    variant="text"
                    disabled={selected.length == 0}
                    onClick={handleApply}
                />
                <ButtonCancel
                    variant="text"
                    onClick={onCancel}
                />
            </Grid>
        </Grid>
    );
};
